import dayjs from "dayjs";
import * as Sentry from "@sentry/react";

import {
  Button,
  Container,
  useTheme,
  Box,
  Stack,
  Divider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckoutContext } from "../../context/checkout.context";
import { TripFiltersContext } from "../../context/trip-filters.context";
import { placeNewOrder } from "../../service/order.service";
import { UserContext } from "../../context/user.context";
import { convertBrazilianDateToObject } from "../../service/date.service";
import { validateCpf } from "../../utils/form.utils";

import FormField from "../../components/form-field/form-field.component";
import PassengerForm from "../../components/passenger-form/passenger-form.component";
import BasicInsuranceOffer from "../../components/basic-insurance-offer/basic-insurance-offer.component";
import BillingForm from "../../components/billing-form/billing-form.component";
import PaymentMethodSelector from "../../components/payment-method-selector/payment-method-selector.component";
import PurchaseDetails from "../../components/purchase-details/purchase-details.component";
import CreditCardForm from "../../components/credit-card-form/credit-card-form.component";
import EmergencyContactForm from "../../components/emergency-contact-form/emergency-contact-form.component";
import Loader from "../../components/loader/loader.component";
import { cleanPhoneMask } from "../../utils/string.utils";
import LinkButton from "../../components/link-button/link-button.component";
import { MobileCheckout } from "../../components/mobile-checkout/mobile-checkout.component";

const MOKED_DATA = {
  currentUser: {
    firstName: "Edson",
    lastName: "Archela",
    email: "techarcapps@gmail.com",
    phone: "11999999999",
  },
  selectedInsurance: {
    insuranceCompany: "affinity",
    insuranceId: "21338",
    insuranceCode: null,
    insuranceName: "Affinity",
    ageInfo: "dummy text",
    coverageUrl: "dummy text",
    insuranceType: "basic",
    healthCoverage: "EUR30.000",
    isCovidCovered: false,
    luggageCoverage: "EUR800",
    oldPassagerPrice: 0,
    passagerPrice: 103.012999008,
  },

  destination: "Ásia",
  boardingDate: new Date(),
  arrivalDate: new Date(),
  youngPassengers: 1,
  oldPassengers: 0,
  passengerPrice: 103.01299008,
  oldPassengerPrice: 0,
  subtotal: 103.01299008,
  discount: 0,
  total: 103.01299008,
};

const Checkout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  //TODO: just for mocking
  const {
    orderId,
    setOrderId,
    selectedInsurance,
    passengers,
    setPassengers,
    addPassenger,
    removePassenger,
    billingInfo,
    setBillingInfo,
    paymentInfo,
    setPaymentInfo,
    countPassengersByAge,
    youngPassengers,
    oldPassengers,
    emergencyContact,
    setEmergencyContact,
    clearCreditCardInfo,
  } = useContext(CheckoutContext);
  // const {
  //   orderId,
  //   setOrderId,
  //   passengers,
  //   setPassengers,
  //   addPassenger,
  //   removePassenger,
  //   billingInfo,
  //   setBillingInfo,
  //   paymentInfo,
  //   setPaymentInfo,
  //   countPassengersByAge,
  //   emergencyContact,
  //   setEmergencyContact,
  //   clearCreditCardInfo,
  // } = useContext(CheckoutContext);
  // const { selectedInsurance, youngPassengers, oldPassengers } = MOKED_DATA;

  //NOTE: ADDED FOR DISCOUNT CUPOM
  const [discountCupom, setDiscountCupom] = useState("");
  const [discountCupomError, setDiscountCupomError] = useState("");

  const { boardingDate, arrivalDate, destination } =
    useContext(TripFiltersContext);

  const [isLoading, setIsLoading] = useState(false);
  const [billingErrors, setBillingErrors] = useState({});
  const [emergencyContactErrors, setEmergencyContactErrors] = useState({});
  const [creditCardFormErrors, setCreditCardFormErrors] = useState({});

  //NOTE: just for mocking
  const { currentUser, authUser } = useContext(UserContext);
  // const { authUser } = useContext(UserContext);
  // const currentUser = MOKED_DATA.currentUser;

  const { passengerPrice, oldPassengerPrice } = selectedInsurance;
  const {
    method: paymentMethod,
    creditCardInfo,
    subtotal,
    discount,
    total,
  } = paymentInfo;

  const isCreditCard = paymentMethod === "credit-card";

  useEffect(() => {
    if (!selectedInsurance?.insuranceId) {
      navigate("/");
    } else if (!currentUser) {
      navigate("/authentication");
    }
    return clearCreditCardInfo;
  }, []);

  useEffect(() => {
    countPassengersByAge();
  }, [passengers]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  if (!selectedInsurance?.insuranceId) return;

  const handleRemovePassengerClick = (passenger) => {
    removePassenger(passenger);
  };

  const handleAddPassengerClick = () => {
    addPassenger();
  };

  const handlePassengerChange = (idx, passenger) => {
    const updatedPassengers = [...passengers];
    updatedPassengers.splice(idx, 1, passenger);
    setPassengers(updatedPassengers);
  };

  const handleCopyPassengerInfoClick = () => {
    setBillingInfo({ ...billingInfo, ...passengers[0] });
  };

  const handleBillingFormChange = (billingForm) => {
    setBillingInfo(billingForm);
  };

  const handlePaymentMethodSelection = (method) => {
    setPaymentInfo({ ...paymentInfo, method: method });
  };

  const handleCreditCardFormChange = (info) => {
    setPaymentInfo({ ...paymentInfo, creditCardInfo: info });
  };

  const handlePassengerBirthDateChange = () => {
    countPassengersByAge();
  };

  const handleEmergencyContactFormChange = (emergencyContact) => {
    setEmergencyContact(emergencyContact);
  };

  const validatePassengers = () => {
    let isValid = true;

    let newPassengers = [...passengers];

    newPassengers.map((passenger) => {
      delete passenger.errors;
      const { cpf, phone, birthDate } = passenger;

      let maxAgeBirthDate = dayjs().subtract(85, "year");
      let passengerBirthDate = convertBrazilianDateToObject(birthDate);

      if (passengerBirthDate.isBefore(maxAgeBirthDate)) {
        passenger.errors = {
          ...passenger.errors,
          birthDate: "Idade maior que 85 anos!",
        };

        isValid = false;
      }

      if (!validateCpf(cpf)) {
        passenger.errors = {
          ...passenger.errors,
          cpf: "CPF inválido!",
        };

        isValid = false;
      }

      if (cleanPhoneMask(phone).length < 11) {
        passenger.errors = {
          ...passenger.errors,
          phone: "Número inválido!",
        };

        isValid = false;
      }
    });

    setPassengers([...newPassengers]);

    return isValid;
  };

  const validateBilling = () => {
    let isValid = true;
    let errors = {};
    const { cpf, phone } = billingInfo;

    if (!validateCpf(cpf)) {
      errors = {
        ...errors,
        cpf: "CPF inválido!",
      };
      isValid = false;
    }

    if (cleanPhoneMask(phone).length < 11) {
      errors = {
        ...errors,
        phone: "Número inválido!",
      };
      isValid = false;
    }

    setBillingErrors(errors);
    return isValid;
  };

  const validateCreditCardForm = () => {
    let isValid = true;
    let errors = {};
    const { fullName, number, banner } = creditCardInfo;

    if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(fullName)) {
      errors = {
        ...errors,
        fullName: "Apenas letras são permitidas",
      };

      isValid = false;
    }

    // Check card number length based on card type
    const isAmex =
      banner &&
      ["amex", "american express"].some((b) =>
        banner.toLowerCase().includes(b)
      );
    const expectedLength = isAmex ? 15 : 16;
    const cleanNumber = cleanPhoneMask(number);

    // Send debug info to Sentry for credit card validation
    Sentry.captureMessage("Credit Card Validation Debug", {
      level: "info",
      contexts: {
        credit_card_validation: {
          banner,
          isAmex,
          number: number ? `${number.substring(0, 4)}****` : "undefined",
          cleanNumber: cleanNumber
            ? `${cleanNumber.substring(0, 4)}****`
            : "undefined",
          cleanNumberLength: cleanNumber.length,
          expectedLength,
          isValid: cleanNumber.length === expectedLength,
        },
      },
      tags: {
        validation_type: "credit_card",
        card_brand: banner || "unknown",
      },
    });

    if (cleanNumber.length !== expectedLength) {
      errors = {
        ...errors,
        number: "Número de cartão inválido",
      };

      isValid = false;
    }

    setCreditCardFormErrors(errors);
    return isValid;
  };

  const validateForm = () => {
    let isPassengersFormValid = validatePassengers();
    let isBillingFormValid = validateBilling();
    let isCreditCardFormValid = validateCreditCardForm();
    //TODO:IMPLEMENTAR
    let isValidDiscountCupom = validateDiscountCupom();
    let isValid =
      isPassengersFormValid && isBillingFormValid && isCreditCardFormValid;

    if (cleanPhoneMask(emergencyContact.phone).length < 11) {
      setEmergencyContactErrors({
        phone: "Número inválido!",
      });
      isValid = false;
    }

    return isValid;
  };

  const validateDiscountCupom = () => {
    setDiscountCupomError("Cupom inválido");
    return false;
  };

  const handleDiscountCupomChange = (event) => {
    setDiscountCupom(event.target.value);
  };

  // Helper function to trim all string values in an object recursively
  const trimStrings = (obj) => {
    if (typeof obj === "string") {
      return obj.trim();
    } else if (Array.isArray(obj)) {
      return obj.map(trimStrings);
    } else if (obj !== null && typeof obj === "object") {
      const trimmedObj = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          trimmedObj[key] = trimStrings(obj[key]);
        }
      }
      return trimmedObj;
    }
    return obj;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const token = await authUser.getIdToken();

    setIsLoading(true);
    if (validateForm()) {
      // Trim all string fields before submitting
      const trimmedOrderData = trimStrings({
        id: orderId,
        userId: currentUser.uid,
        passengers: passengers.map((passenger) => {
          // Log the incoming date for debugging
          console.log("Original birthDate:", passenger.birthDate);

          // Convert the date string to dayjs object
          const birthDate = dayjs(passenger.birthDate, "DD/MM/YYYY");

          // Verify if the date is valid
          if (!birthDate.isValid()) {
            console.error("Invalid date:", passenger.birthDate);
            throw new Error(
              `Invalid birth date for passenger: ${passenger.name}`
            );
          }

          const fDate = birthDate.format("YYYY-MM-DD");
          console.log("Converted birthDate:", fDate);
          return {
            ...passenger,
            birthDate: fDate,
          };
        }),
        insurance: selectedInsurance,
        billingInfo,
        paymentInfo,
        emergencyContact,
        tripInfo: {
          boardingDate: boardingDate ? boardingDate.format("YYYY-MM-DD") : null,
          arrivalDate: arrivalDate ? arrivalDate.format("YYYY-MM-DD") : null,
          destination,
        },
      });

      placeNewOrder(trimmedOrderData, token)
        .then((res) => {
          setIsLoading(false);
          setOrderId(null);

          const { vouchers, paymentInfo } = res.data;
          const { qrCodeBase64, qrCodeString } = paymentInfo;
          navigate("/order-feedback", {
            state: { vouchers, qrCodeBase64, qrCodeString },
          });
        })
        .catch((error) => {
          setIsLoading(false);

          // Capture detailed error context with Sentry
          Sentry.withScope((scope) => {
            // Add user context
            if (currentUser) {
              scope.setUser({
                id: currentUser.uid,
                email: currentUser.email,
              });
            }

            // Add request context
            scope.setContext("order_request", {
              orderId,
              passengersCount: passengers.length,
              insuranceId: selectedInsurance?.insuranceId,
              insuranceCompany: selectedInsurance?.insuranceCompany,
              paymentMethod,
              tripDestination: destination,
              boardingDate: boardingDate?.toString(),
              arrivalDate: arrivalDate?.toString(),
              total,
              subtotal,
              discount,
            });

            // Add response context if available
            if (error.response) {
              scope.setContext("error_response", {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
                headers: error.response.headers,
              });
            }

            // Add request context if available
            if (error.config) {
              scope.setContext("error_request", {
                method: error.config.method,
                url: error.config.url,
                baseURL: error.config.baseURL,
              });
            }

            // Set error level based on status
            const status = error.response?.status;
            scope.setLevel(status === 402 ? "warning" : "error");

            // Capture the exception
            Sentry.captureException(error, {
              tags: {
                errorType: "order_placement",
                paymentMethod,
                insuranceCompany: selectedInsurance?.insuranceCompany,
              },
            });
          });

          const { data, status } = error.response || {};
          const { orderId: errorOrderId, errorMessage } = data || {};

          if (errorOrderId) {
            setOrderId(errorOrderId);
          }

          if (status === 402) {
            // Use specific error message from API if available, otherwise fallback to generic
            const paymentErrorMessage =
              errorMessage ||
              "Ocorreu um erro com o pagamento, tente novamente.";
            alert(paymentErrorMessage);
          } else {
            // For other errors, use specific message if available
            const generalErrorMessage =
              errorMessage ||
              "Ocorreu um erro com o seu pedido, entre em contato com o nosso suporte.";
            alert(generalErrorMessage);
          }
        });
    } else {
      setIsLoading(false);
      window.scrollTo(0, 0);
    }
  };

  const checkoutProps = {
    passengers,
    handlePassengerChange,
    handlePassengerBirthDateChange,
    handleRemovePassengerClick,
    handleAddPassengerClick,
    emergencyContact,
    emergencyContactErrors,
    handleEmergencyContactFormChange,
    handleCopyPassengerInfoClick,
    billingInfo,
    billingErrors,
    handleBillingFormChange,
    paymentMethod,
    handlePaymentMethodSelection,
    isCreditCard,
    total,
    creditCardInfo,
    creditCardFormErrors,
    handleCreditCardFormChange,
    selectedInsurance,
    destination,
    boardingDate,
    arrivalDate,
    youngPassengers,
    oldPassengers,
    passengerPrice,
    oldPassengerPrice,
    subtotal,
    discount,
    handleDiscountCupomChange,
    discountCupom,
    discountCupomError,
    validatePassengers,
    validateBilling,
    setBillingErrors,
    setPassengers,
    setEmergencyContactErrors,
  };

  return (
    <div>
      {isLoading ? (
        <Loader className="checkout-loader" />
      ) : isMobile ? (
        <MobileCheckout
          chekoutProps={checkoutProps}
          handleSubmit={handleSubmit}
        />
      ) : (
        <Container
          sx={{ margin: "4rem auto", display: { xs: "none", md: "block" } }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h3"
              sx={{ color: `${theme.palette.primary.main}` }}
            >
              Identificação dos Segurados
            </Typography>
            <Link
              to="/search-results"
              style={{
                textDecoration: "underline",
                textDecorationColor: `${theme.palette.grey[100]}`,
              }}
            >
              <Typography variant="body1" sx={{ color: "black" }}>
                Voltar para a pesquisa anterior
              </Typography>
            </Link>
          </Box>
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "2rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  flexGrow: 1,
                }}
              >
                <Stack direction={"column"} sx={{ margin: "2rem 0" }}>
                  {passengers.map((passenger, idx) => (
                    <div key={idx}>
                      <PassengerForm
                        id={idx}
                        passenger={passenger}
                        onChange={handlePassengerChange}
                        onRemove={handleRemovePassengerClick}
                        onBirthDateChange={handlePassengerBirthDateChange}
                      />
                      <Divider
                        sx={{
                          borderColor: `${theme.palette.primary.main}`,
                          margin: "1rem 0",
                        }}
                      />
                    </div>
                  ))}

                  <Button
                    variant="contained"
                    onClick={handleAddPassengerClick}
                    sx={{
                      borderRadius: "4px",
                      py: 1.5,
                      textTransform: "none",
                    }}
                  >
                    <Typography variant="body1">
                      Adicionar mais passageiros +
                    </Typography>
                  </Button>
                </Stack>
                <Typography variant="h3">
                  Dados do contato de emergência
                </Typography>

                <EmergencyContactForm
                  emergencyContact={emergencyContact}
                  errors={emergencyContactErrors}
                  onChange={handleEmergencyContactFormChange}
                />
                <Box sx={{ marginBottom: "1rem" }}>
                  <Typography
                    variant="h3"
                    sx={{ color: `${theme.palette.primary.main}` }}
                  >
                    Dados para receber os vouchers
                  </Typography>

                  <LinkButton
                    onClick={handleCopyPassengerInfoClick}
                    sx={{ color: "black" }}
                  >
                    Utilizar dados do 1° segurado
                  </LinkButton>
                </Box>
                <BillingForm
                  billingInfo={billingInfo}
                  errors={billingErrors}
                  onChange={handleBillingFormChange}
                />

                <Typography
                  variant="h3"
                  sx={{ color: `${theme.palette.primary.main}` }}
                >
                  Forma de pagamento
                </Typography>

                <PaymentMethodSelector
                  selected={paymentMethod}
                  onSelect={handlePaymentMethodSelection}
                />
                {isCreditCard && (
                  <CreditCardForm
                    value={total}
                    creditCardInfo={creditCardInfo}
                    errors={creditCardFormErrors}
                    onChange={handleCreditCardFormChange}
                    insuranceCompany={selectedInsurance.insuranceCompany}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  width: "fit-content",
                  minWidth: { xs: "100%", md: "380px" },
                }}
              >
                <BasicInsuranceOffer
                  insurance={selectedInsurance}
                  destination={destination}
                  boardingDate={boardingDate}
                  arrivalDate={arrivalDate}
                />
                <PurchaseDetails
                  youngPassengers={youngPassengers}
                  oldPassengers={oldPassengers}
                  passengerPrice={passengerPrice}
                  oldPassengerPrice={oldPassengerPrice}
                  subtotal={subtotal}
                  discount={discount}
                  total={total}
                />

                {/* Campo de cupom escondido até existir uma campanha de cupom
                    real (a validação ainda não foi implementada). */}
                {/* <FormField
                  label="Cupom"
                  name="discountCupom"
                  variant="light"
                  onChange={handleDiscountCupomChange}
                  value={discountCupom}
                  error={discountCupomError}
                /> */}

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    borderRadius: "4px",
                    py: 1.5,
                    textTransform: "none",
                    width: "100%",
                    backgroundColor: "secondary.main",
                    color: "black",
                    fontWeight: "bolder",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                    Finalizar pedido
                  </Typography>
                </Button>
                <Typography variant="body2">
                  Ao clicar em Finalizar pedido, concordo que li e aceito
                  os&nbsp;
                  <a
                    className="terms-link"
                    href="https://drive.google.com/file/d/1U3CE3VDg1-7lMKkdTSVyjQepqD23hYOK/view"
                    target="_blank"
                    rel="noreferrer"
                  >
                    termos de uso, condições gerais e coberturas
                  </a>
                </Typography>
              </Box>
            </Box>
          </form>
        </Container>
      )}
    </div>
  );
};

export default Checkout;
